import { useMemo } from 'react'
import {
  PolicyRulesJson,
  EmployeeData,
  FamilyMember,
  TopUpSelection,
  PremiumBreakdown,
  PremiumType,
  AgeBand,
  TopUpItem,
  FlexiItem,
} from '../types'

// ── Helpers ──────────────────────────────────────────────────

/** Strip time component and parse to a comparable ms number.
 *  Normalises "2026-04-01T00:00:00", "2026-04-01", "01-04-2026", "01/04/2026" etc. */
const parseDateOnlyToMs = (dateString: string | null | undefined): number | null => {
  if (!dateString) return null
  const dateOnly = dateString.split('T')[0]?.trim()
  if (!dateOnly) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
    const [year, month, day] = dateOnly.split('-').map((p) => Number(p))
    if ([year, month, day].some((n) => Number.isNaN(n))) return null
    return Date.UTC(year, month - 1, day)
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateOnly)) {
    const [day, month, year] = dateOnly.split('-').map((p) => Number(p))
    if ([year, month, day].some((n) => Number.isNaN(n))) return null
    return Date.UTC(year, month - 1, day)
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateOnly)) {
    const [day, month, year] = dateOnly.split('/').map((p) => Number(p))
    if ([year, month, day].some((n) => Number.isNaN(n))) return null
    return Date.UTC(year, month - 1, day)
  }
  return null
}

const calculateAge = (dob: string, referenceDate: string): number => {
  const birth = new Date(dob)
  const ref = new Date(referenceDate)
  if (isNaN(birth.getTime()) || isNaN(ref.getTime())) return 0
  let age = ref.getFullYear() - birth.getFullYear()
  const birthdayThisYear = new Date(ref.getFullYear(), birth.getMonth(), birth.getDate())
  if (ref < birthdayThisYear) {
    age--
  }
  return Math.max(0, age)
}

const findAgeBand = (age: number, ageBands: AgeBand[]): AgeBand | null => {
  return ageBands.find((b) => age >= b.min && age <= b.max) ?? null
}

const evaluateFormula = (formula: string, variables: Record<string, number>): number => {
  try {
    let expr = formula
    for (const [key, val] of Object.entries(variables)) {
      expr = expr.replace(new RegExp(key, 'g'), String(val))
    }
    // Safe math-only eval: only allow digits, operators, parens, Math functions
    if (!/^[\d\s+\-*/().,%minMaxMath]*$/.test(expr)) return 0
    const fn = new Function(`return Math.round(${expr})`)
    return fn() as number
  } catch {
    return 0
  }
}

// ── Hook ─────────────────────────────────────────────────────

interface UsePremiumCalculationProps {
  json: PolicyRulesJson | null
  employee: EmployeeData | null
  selectedPlanCode: string | null
  selectedSICode: string | null
  members: FamilyMember[]
  selectedTopUps: TopUpSelection[]
  selectedFlexiCodes: string[]
}

export function usePremiumCalculation({
  json,
  employee,
  selectedPlanCode,
  selectedSICode,
  members,
  selectedTopUps,
  selectedFlexiCodes,
}: UsePremiumCalculationProps): PremiumBreakdown {
  return useMemo(() => {
    const empty: PremiumBreakdown = {
      basePremium: 0,
      baseGst: 0,
      baseTotal: 0,
      topupPremium: 0,
      topupGst: 0,
      topupTotal: 0,
      flexiPremium: 0,
      flexiGst: 0,
      flexiTotal: 0,
      totalGst: 0,
      totalPremiumPayable: 0,
      proRataFactor: 1,
      memberPremiums: {},
      walletAmount: 0,
      finalPayable: 0,
    }

    if (!json || !employee || !selectedPlanCode || !selectedSICode || members.length === 0)
      return empty

    // ── Wallet Amount (grade-wise, from policy setup) ──────
    const walletConfig = json.walletConfig
    const walletEnabled = walletConfig?.enabled ?? false
    const walletAmount = walletEnabled
      ? (walletConfig?.gradeConfigs.find((g) => g.gradeCode === employee.Grade)?.walletAmount ?? 0)
      : 0

    const { basics, employee: emp, topUps, flexi, sumInsured } = json
    const gstRate = basics.gstRate ?? 0.18
    const policyStart = basics.startDateUtc

    // ── Pro-Rata Factor ───────────────────────────────────
    let proRataFactor = 1
    if (basics.proRata?.enabled && employee.DOJ) {
      const dojMs = parseDateOnlyToMs(employee.DOJ)
      const effectiveMs = parseDateOnlyToMs(basics.startDateUtc)
      const expiryMs = parseDateOnlyToMs(basics.endDateUtc)
      if (dojMs !== null && effectiveMs !== null && expiryMs !== null && dojMs > effectiveMs) {
        const msPerDay = 86400000
        const daysRemaining = Math.floor((expiryMs - dojMs) / msPerDay) + 1
        proRataFactor = Math.min(1, Math.max(0, daysRemaining / 365))
      }
    }

    // ── Base Premium ──────────────────────────────────────
    let basePremium = 0
    let memberPremiums: Record<number, number> = {}

    // For ParentGroup policies, skip premium if no non-Self members (no parents added yet)
    const selectedPlanDef = json.plans.plans.find((p) => p.planCode === selectedPlanCode)
    const isParentGroup = selectedPlanDef?.groupingModel === 'ParentGroup'
    const hasNonSelfMembers = members.some((m) => m.relation !== 'Self')

    const basisEntry = emp.premiumBasis.perSumInsured.find(
      (e) => e.sumInsuredOptionCode === selectedSICode
    )
    const table = emp.premiumTables.tables.find(
      (t) => t.planCode === selectedPlanCode && t.sumInsuredOptionCode === selectedSICode
    )
    const ageBands = emp.ageBands.ageBands

    if (basisEntry && table && !(isParentGroup && !hasNonSelfMembers)) {
      const premiumType: PremiumType = basisEntry.premiumType

      if (premiumType === 'FamilyFloater') {
        let referenceDob = employee.DOB
        if (basisEntry.familyDobBasis === 'OldestMemberDob') {
          const oldest = members.reduce((prev, curr) => {
            const prevDate = new Date(prev.dob)
            const currDate = new Date(curr.dob)
            return currDate < prevDate ? curr : prev
          }, members[0])
          if (oldest) referenceDob = oldest.dob
        }
        const age = calculateAge(referenceDob, policyStart)
        const band = findAgeBand(age, ageBands)
        if (band && table.family) {
          const entry = table.family.find((f) => f.bandCode === band.bandCode)
          if (entry) basePremium = entry.premiumAmount
        }
      } else if (premiumType === 'Individual') {
        const perMember: Record<number, number> = {}
        if (table.individualCommon && table.individualCommon.length > 0) {
          basePremium = members.reduce((sum, member) => {
            const age = calculateAge(member.dob, policyStart)
            const band = findAgeBand(age, ageBands)
            if (band) {
              const entry = table.individualCommon!.find((e) => e.bandCode === band.bandCode)
              if (entry) {
                perMember[member.id] = entry.premiumAmount
                return sum + entry.premiumAmount
              }
            }
            perMember[member.id] = 0
            return sum
          }, 0)
        } else if (table.individualRelationWise && table.individualRelationWise.length > 0) {
          basePremium = members.reduce((sum, member) => {
            const age = calculateAge(member.dob, policyStart)
            const band = findAgeBand(age, ageBands)
            if (band) {
              const entry = table.individualRelationWise!.find(
                (e) => e.relation === member.relation && e.bandCode === band.bandCode
              )
              if (entry) {
                perMember[member.id] = entry.premiumAmount
                return sum + entry.premiumAmount
              }
            }
            perMember[member.id] = 0
            return sum
          }, 0)
        }
        memberPremiums = perMember
      } else if (premiumType === 'CountBased') {
        if (table.countBased) {
          // Group members by relation
          const relationCounts: Record<string, number> = {}
          members.forEach((m) => {
            relationCounts[m.relation] = (relationCounts[m.relation] || 0) + 1
          })
          for (const cbEntry of table.countBased) {
            const count = relationCounts[cbEntry.relation] ?? 0
            if (count === 0) continue
            const slab = cbEntry.slabs.find((s) => s.count === count)
            if (slab) basePremium += slab.premiumAmount
          }
        }
      }
    }

    // Apply pro-rata. For Individual pricing, prorate the per-member values too so
    // the row badges match the summary total after reopen and during live edits.
    if (basisEntry?.premiumType === 'Individual' && Object.keys(memberPremiums).length > 0) {
      const proratedMemberPremiums = Object.entries(memberPremiums).reduce<Record<number, number>>(
        (acc, [memberId, premiumAmount]) => {
          acc[Number(memberId)] = Math.round(premiumAmount * proRataFactor)
          return acc
        },
        {}
      )
      memberPremiums = proratedMemberPremiums
      basePremium = Object.values(proratedMemberPremiums).reduce((sum, premiumAmount) => sum + premiumAmount, 0)
    } else {
      basePremium = Math.round(basePremium * proRataFactor)
    }

    // ── Top-Up Premium ────────────────────────────────────
    let topupPremium = 0

    if (topUps?.enabled && selectedTopUps.length > 0) {
      for (const sel of selectedTopUps) {
        const item = topUps.items.find((i) => i.itemCode === sel.itemCode)
        if (!item) continue
        const tier = item.sumInsuredOptions.find((o) => o.optionCode === sel.selectedTierCode)
        if (!tier) continue

        let itemPremium = 0

        const resolvePremium = (premAmt: number, planPremiums: typeof tier.planPremiums): number => {
          if (planPremiums) {
            const match = planPremiums.find((p) => p.planCode === selectedPlanCode)
            if (match) return match.premiumAmount
          }
          return premAmt
        }

        if (item.premiumCalculation.type === 'PerOption') {
          itemPremium = resolvePremium(tier.premiumAmount, tier.planPremiums)
        } else if (item.premiumCalculation.type === 'Calculated' && item.premiumCalculation.formula) {
          const si = tier.fixedAmount ?? computeSI(tier, employee, sumInsured)
          itemPremium = evaluateFormula(item.premiumCalculation.formula, { SumInsured: si })
        } else if (item.premiumCalculation.type === 'FixedAmount' && item.premiumCalculation.fixedAmount != null) {
          itemPremium = item.premiumCalculation.fixedAmount
        }

        if (item.premiumModel === 'PerMember') {
          itemPremium = itemPremium * members.length
        }

        topupPremium += itemPremium
      }
      topupPremium = Math.round(topupPremium * proRataFactor)
    }

    // ── Flexi Premium ─────────────────────────────────────
    let flexiPremium = 0

    if (flexi?.enabled && selectedFlexiCodes.length > 0) {
      for (const code of selectedFlexiCodes) {
        const item = flexi.items.find((i) => i.itemCode === code)
        if (!item) continue
        if (item.planPremiums) {
          const match = item.planPremiums.find((p) => p.planCode === selectedPlanCode)
          if (match) {
            flexiPremium += match.premiumAmount
            continue
          }
        }
        flexiPremium += item.premiumAmount
      }
      flexiPremium = Math.round(flexiPremium * proRataFactor)
    }

    // ── GST & Totals ─────────────────────────────────────
    const baseGst = Math.round(basePremium * gstRate)
    const topupGst = Math.round(topupPremium * gstRate)
    const flexiGst = Math.round(flexiPremium * gstRate)

    const baseTotal = basePremium + baseGst
    const topupTotal = topupPremium + topupGst
    const flexiTotal = flexiPremium + flexiGst

    return {
      basePremium,
      baseGst,
      baseTotal,
      topupPremium,
      topupGst,
      topupTotal,
      flexiPremium,
      flexiGst,
      flexiTotal,
      totalGst: baseGst + topupGst + flexiGst,
      totalPremiumPayable: baseTotal + topupTotal + flexiTotal,
      proRataFactor,
      memberPremiums,
      walletAmount: walletAmount,
      finalPayable: Math.max(0, baseTotal + topupTotal + flexiTotal - walletAmount),
    }
  }, [json, employee, selectedPlanCode, selectedSICode, members, selectedTopUps, selectedFlexiCodes])
}

// Helper to compute SI from CTC multiplier
function computeSI(
  tier: { type: string; fixedAmount: number | null; multiplier: number | null; minCap: number | null; maxCap: number | null },
  employee: EmployeeData,
  sumInsured: { options: any[] }
): number {
  if (tier.type === 'Fixed' && tier.fixedAmount != null) return tier.fixedAmount
  if (tier.type === 'CtcMultiplier' && tier.multiplier != null && employee.CTC) {
    let val = employee.CTC * tier.multiplier
    if (tier.minCap != null) val = Math.max(val, tier.minCap)
    if (tier.maxCap != null) val = Math.min(val, tier.maxCap)
    return Math.round(val)
  }
  return 0
}

export { calculateAge, findAgeBand }
